import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { useMemo } from 'react';

import { cn } from '@distribute/frontend/utils';

import { SNIPPETS_ROUTE, useSelectedTab } from '../../../entities/history';

import { navigate } from '../../../processes/navigation';
import { IconMap } from '../../../shared/sprite';
import { Tabs } from '../../../shared/ui';

import {
  useShareSnippetHandler,
  useSnippetsActions,
  useSnippetsFilters,
} from '../hooks';
import { SnippetsTab } from '../lib';

import {
  snippetsModel,
  DeleteSnippetConfirmationModal,
  SnippetSettingsModal,
  SnippetPreviewModal,
  ReplaceSnippetModal,
} from '../../../features/snippets';

import { useSnippetsPermissions } from '../../../features/teams';

import { SnippetsTeam } from './SnippetsTeam';
import { Snippets } from './Snippets';
import { TeamOnlyAlert } from '../../../entities/share';
import { SnippetsFilters } from './SnippetsFilters';

export const SnippetsContent = () => {
  const {
    filter,
    filterByOwner,
    sort,
    setFilter,
    setFilterByOwner,
    setSort,
    isFiltersActive,
  } = useSnippetsFilters();

  const dispatch = useDispatch();

  const modal = useSelector(snippetsModel.selectors.selectModal);

  const { isCanManageOrCreatePersonalSnippets } = useSnippetsActions();
  const { isCanCreatePersonalSnippets } = useSnippetsPermissions();

  const categories = useSelector(snippetsModel.selectors.selectCategories);

  const { isForbidden } = useShareSnippetHandler({
    snippets: categories.flatMap((category) => category.snippets),
  });

  const personalSnippets = useSelector(
    snippetsModel.selectors.selectPersonalSnippets
  );
  const draftSnippets = useSelector(
    snippetsModel.selectors.selectDraftSnippets
  );

  const tabs = useMemo(
    () =>
      [
        {
          name: SnippetsTab.TEAM,
          title: 'Team',
          icon: IconMap.TeamAccess,
          component: (
            <SnippetsTeam
              categories={categories}
              isFiltersActive={isFiltersActive}
              sort={sort}
              filter={filter}
              filterByOwner={filterByOwner}
            />
          ),
        },
        {
          name: SnippetsTab.PERSONAL,
          title: 'Personal',
          icon: IconMap.EyeCrossedBold,
          component: (
            <Snippets
              snippets={personalSnippets}
              type={SnippetsTab.PERSONAL}
              sort={sort}
              filter={filter}
            />
          ),
          isHide: !isCanCreatePersonalSnippets,
        },
        {
          name: SnippetsTab.DRAFTS,
          title: 'Drafts',
          icon: IconMap.DraftPencil,
          component: (
            <Snippets
              snippets={draftSnippets}
              type={SnippetsTab.DRAFTS}
              sort={sort}
              filter={filter}
            />
          ),
          isHide: !isCanManageOrCreatePersonalSnippets,
        },
      ].filter((tab) => !tab.isHide),
    [
      isCanManageOrCreatePersonalSnippets,
      isCanCreatePersonalSnippets,
      isFiltersActive,
      sort,
      filter,
      filterByOwner,
      categories,
      personalSnippets,
      draftSnippets,
    ]
  );

  const { selectedTab } = useSelectedTab(tabs);

  return (
    <>
      {modal && (
        <>
          <SnippetPreviewModal
            isOpen={modal.type === 'snippetPreview'}
            snippet={modal.snippet}
            onClose={() => dispatch(snippetsModel.actions.setModal(null))}
            onOpenDeleteSnippetModal={() =>
              dispatch(
                snippetsModel.actions.setModal({
                  type: 'deleteSnippet',
                  snippet: modal.snippet,
                })
              )
            }
            onOpenReplaceSnippetModal={() =>
              dispatch(
                snippetsModel.actions.setModal({
                  type: 'replaceSnippet',
                  snippet: modal.snippet,
                })
              )
            }
          />
          <DeleteSnippetConfirmationModal
            isOpen={modal.type === 'deleteSnippet'}
            onClose={() => dispatch(snippetsModel.actions.setModal(null))}
            snippet={modal.snippet}
          />
          <SnippetSettingsModal
            isOpen={modal.type === 'snippetSettings'}
            onClose={() => dispatch(snippetsModel.actions.setModal(null))}
            snippet={modal.snippet}
          />
          <ReplaceSnippetModal
            isOpen={modal.type === 'replaceSnippet'}
            snippet={modal.snippet}
            onClose={() => dispatch(snippetsModel.actions.setModal(null))}
          />
        </>
      )}
      {isForbidden && <TeamOnlyAlert />}
      <div className="flex flex-col flex-grow-1 gap-y-6 sm:pb-0 pb-6">
        <div
          className={cn(
            'flex flex-nowrap items-end gap-4 md:justify-end md:flex-col min-h-10',
            { hidden: !isCanManageOrCreatePersonalSnippets }
          )}
        >
          <Tabs
            selectedTab={selectedTab}
            tabs={tabs}
            tabsOnly
            onSelectTab={(tab) =>
              dispatch(navigate({ to: `${SNIPPETS_ROUTE}/${tab.name}` }))
            }
            className="!mb-0"
          />
          <SnippetsFilters
            type={
              isCanManageOrCreatePersonalSnippets
                ? (selectedTab.name as SnippetsTab)
                : SnippetsTab.TEAM
            }
            sort={sort}
            filter={filter}
            filterByOwner={filterByOwner}
            onSortChange={setSort}
            onFilterChange={setFilter}
            onOwnerChange={setFilterByOwner}
          />
        </div>
        {isCanManageOrCreatePersonalSnippets ? (
          <Switch>
            {tabs.map((tab) => (
              <Route
                path={`${SNIPPETS_ROUTE}/${tab.name}`}
                key={tab.name}
                render={() => tab.component}
              />
            ))}
          </Switch>
        ) : (
          <SnippetsTeam
            categories={categories}
            isFiltersActive={isFiltersActive}
            sort={sort}
            filter={filter}
            filterByOwner={filterByOwner}
          />
        )}
      </div>
    </>
  );
};
